import { CSSResult, html, PropertyValues, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property } from 'lit/decorators.js';

import { Component } from '../../models';
import { FocusTrapMixin } from '../../utils/mixins/focus/FocusTrapMixin';
import { PreventScrollMixin } from '../../utils/mixins/PreventScrollMixin';
import { TYPE, VALID_TEXT_TAGS } from '../text/text.constants';
import { BUTTON_VARIANTS, ICON_BUTTON_SIZES } from '../button/button.constants';
import { FooterMixin } from '../../utils/mixins/FooterMixin';
import { BackdropMixin } from '../../utils/mixins/BackdropMixin';
import providerUtils from '../../utils/provider';
import ResponsiveSettingsContext from '../responsivesettingsprovider/responsiveSettingsContext';
import ResponsiveSettingsProvider from '../responsivesettingsprovider';
import { DepthManager, StackChange, type StackedOverlayComponent } from '../../utils/controllers/DepthManager';
import { KeyDownHandledMixin } from '../../utils/mixins/KeyDownHandledMixin';
import { ACTIONS, KeyToActionMixin } from '../../utils/mixins/KeyToActionMixin';

import { DEFAULTS } from './dialog.constants';
import type { DialogRole, DialogSize, DialogVariant } from './dialog.types';
import { DialogEventManager } from './dialog.events';
import styles from './dialog.styles';

/**
 * Dialog component is a modal dialog that can be used to display information or prompt the user for input.
 * It can be used to create custom dialogs where content for the body and footer actions is provided by the consumer.
 * The dialog is available in 5 sizes: small, medium, large, xlarge and fullscreen. It may also receive custom styling/sizing.
 * The dialog interrupts the user and will block interaction with the rest of the application until it is closed.
 *
 * Dialog component have 2 variants: default and promotional.
 *
 * ## Visibility
 *
 * The dialog can be controlled solely through the `visible` property, no trigger element is required.
 * If a `triggerID` is provided, the dialog will manage focus with that element, otherwise it will
 * remember the previously focused element before the dialog was opened.
 *
 * The dialog is a controlled component, meaning it does not have its own state management for visibility.
 * Use the `visible` property to control the visibility of the dialog.
 * Use the `onClose` event to handle the close action of the dialog (fired when Close button is clicked
 * or Escape is pressed).
 *
 * ## Accessibility
 *
 * Some attributes have to be explicitly set by the consumer of the component:
 *
 * - The dialog should have an aria-label or aria-labelledby attribute to provide a label for screen readers.
 * - Use aria-labelledby to reference the ID of the element that labels the dialog when there is no visible title.
 *
 * ## Responsive design
 *
 * Dialog has few built in logic to prevent content clipping on small screens
 *
 * - maximum height limited to the viewport height
 * - dialog body has `overflow: auto` by default
 * - dialog itself also has `overflow: auto`, it activates only when the body can not shrink more
 *
 *
 * @dependency mdc-button
 * @dependency mdc-text
 *
 * @tagname mdc-dialog
 *
 * @event shown - (React: onShown) Dispatched when the dialog is shown
 * @event hidden - (React: onHidden) Dispatched when the dialog is hidden
 * @event created - (React: onCreated) Dispatched when the dialog is created (added to the DOM)
 * @event destroyed - (React: onDestroyed) Dispatched when the dialog is destroyed (removed from the DOM)
 * @event close - (React: onClose) Dispatched when the Close Button is clicked or Escape key is pressed
 * (this does not hide the dialog)
 *
 * @cssproperty --mdc-dialog-primary-background-color - primary background color of the dialog
 * @cssproperty --mdc-dialog-border-color - border color of the dialog
 * @cssproperty --mdc-dialog-header-text-color - text color of the header/title of the dialog
 * @cssproperty --mdc-dialog-description-text-color - text color of the below header description of the dialog
 * @cssproperty --mdc-dialog-elevation-3 - elevation of the dialog
 * @cssproperty --mdc-dialog-width - width of the dialog
 * @cssproperty --mdc-dialog-height - height of the dialog
 * @cssproperty --mdc-dialog-backdrop-color - background color of the backdrop (if backdrop is enabled)
 *
 * @csspart body - The body section of the dialog.
 * @csspart description-text - The description text of the dialog.
 * @csspart dialog-close-btn - The close button of the dialog.
 * @csspart header - The header of the dialog.
 * @csspart header-section - The header section of the dialog.
 * @csspart header-text - The header text of the dialog.
 *
 * @slot header-prefix - Slot for the dialog header content. This can be used to pass custom header content.
 * @slot dialog-body - Slot for the dialog body content
 * @slot footer-link - This slot is for passing `mdc-link` component within the footer section.
 * @slot footer-button-secondary - This slot is for passing secondary variant of `mdc-button` component
 * within the footer section.
 * @slot footer-button-primary - This slot is for passing primary variant of
 * `mdc-button` component within the footer section.
 * @slot footer -  This slot is for passing custom footer content. Only use this if really needed,
 * using the footer-link and footer-button slots is preferred
 */
class Dialog
  extends KeyDownHandledMixin(
    KeyToActionMixin(BackdropMixin(PreventScrollMixin(FocusTrapMixin(FooterMixin(Component))))),
  )
  implements StackedOverlayComponent
{
  /** @internal */
  protected readonly responsiveSettingsContext = providerUtils.consume<typeof ResponsiveSettingsContext.context>({
    host: this,
    context: ResponsiveSettingsProvider.Context,
    subscribe: true,
    syncProperties: ['size'],
  });

  /** track the depth of the popover for z-index calculation
   * @internal
   */
  private depthManager = new DepthManager(this);

  /**
   * The unique ID of the dialog
   */
  @property({ type: String, reflect: true })
  override id: string = '';

  /**
   * The ID of the element that triggers the dialog
   *
   * @default undefined
   */
  @property({ type: String, reflect: true })
  triggerID?: string;

  /**
   * The visibility of the dialog
   *
   * Dialog is a controlled component, visible is the only property that controls the visibility of the dialog.
   * @default false
   */
  @property({ type: Boolean, reflect: true })
  visible: boolean = DEFAULTS.VISIBLE;

  /**
   * The internal z-index of the dialog.
   * @internal
   */
  private internalZIndex?: number;

  /**
   * The effective z-index of the dialog.
   *
   * If no explicit `z-index` value is provided, then it automatically calculated
   * to ensure proper stacking order among multiple overlays.
   */
  @property({ type: Number, reflect: true, attribute: 'z-index' })
  get zIndex() {
    if (!Number.isInteger(this.internalZIndex)) {
      return this.depthManager.getHostZIndex();
    }
    return this.internalZIndex!;
  }

  set zIndex(value: number) {
    this.internalZIndex = value;
  }

  /**
   * The internal value helps to restore original size when responsive settings disabled.
   *
   * @internal
   */
  protected internalSize: DialogSize = DEFAULTS.SIZE;

  /**
   * The size of the dialog, can be 'small' (432px width), 'medium' (656px width), 'large' (992px width), 'xlarge' (90% width) or 'fullscreen' (100% width).
   * @default small
   */
  @property({ type: String, reflect: true })
  get size(): DialogSize {
    return this.responsiveSettingsContext?.value?.forceFullscreenDialog ? 'fullscreen' : this.internalSize;
  }

  set size(value: DialogSize) {
    this.internalSize = value;
  }

  /**
   * The variant of the dialog, can be 'default' or 'promotional'
   * @default default
   */
  @property({ type: String, reflect: true })
  variant: DialogVariant = DEFAULTS.VARIANT;

  /**
   * Defines a string value for the aria-label attribute for close button accessibility
   */
  @property({ type: String, attribute: 'close-button-aria-label' })
  closeButtonAriaLabel: string | null = null;

  /**
   * Defines a string value for the aria-label attribute when header is not used
   */
  @property({ type: String, reflect: true, attribute: 'aria-label' })
  override ariaLabel: string | null = null;

  /**
   * Defines a string value for the aria-labelledby attribute that refers to the element
   * labeling the dialog for accessibility
   */
  @property({ type: String, reflect: true, attribute: 'aria-labelledby' })
  ariaLabelledby: string | null = null;

  /**
   * Defines a string value for the aria-describedby attribute that refers to the element
   * describing the dialog for accessibility
   */
  @property({ type: String, reflect: true, attribute: 'aria-describedby' })
  ariaDescribedby: string | null = null;

  /**
   * Defines a string value for the aria-description attribute that refers to the element
   * describing the dialog for accessibility
   */
  @property({ type: String, reflect: true, attribute: 'aria-description' })
  override ariaDescription: string | null = null;

  /**
   * Defines a string value to display as the title of the dialog
   */
  @property({ type: String, reflect: true, attribute: 'header-text' })
  headerText?: string;

  /**
   * Defines a string value to display as the under-header description of the dialog
   */
  @property({ type: String, reflect: true, attribute: 'description-text' })
  descriptionText?: string;

  /**
   * The html tag to be used for the header text
   */
  @property({ type: String, reflect: true, attribute: 'header-tag-name' })
  headerTagName: string = DEFAULTS.HEADER_TAG_NAME;

  /**
   * The html tag to be used for the below-header description text
   */
  @property({ type: String, reflect: true, attribute: 'description-tag-name' })
  descriptionTagName: string = DEFAULTS.DESCRIPTION_TAG_NAME;

  /**
   * Role of the dialog
   * @default dialog
   */
  @property({ type: String, reflect: true })
  override role: DialogRole = DEFAULTS.ROLE;

  /**
   * Disable setting the aria-haspopup attribute on trigger element.
   * Make sure to set this to true when the popover is extended and its role
   * is not 'dialog' or 'alertdialog' i.e. listbox, menu, etc.
   * @default false
   */
  @property({ type: Boolean, reflect: true, attribute: 'disable-aria-haspopup' })
  disableAriaHasPopup: boolean = DEFAULTS.DISABLE_ARIA_HAS_POPUP;

  /**
   * Determines whether the focus trap is enabled.
   * If true, focus will be restricted to the content within this component.
   *
   * NOTE: this should only be disabled in rare cases! By default a Modal Dialog
   * should trap focus always.
   *
   * @default true
   */
  @property({ type: Boolean, reflect: true, attribute: 'focus-trap' })
  focusTrap: boolean = DEFAULTS.FOCUS_TRAP;

  /**
   * Determines whether a backdrop should be displayed behind the dialog.
   * By default, a backdrop is present and it will cover the rest of the page and prevent interaction
   * with other elements while the dialog is open.
   * When this attribute is set to true, no backdrop will be created.
   *
   * @default false
   */
  @property({ type: Boolean, reflect: true, attribute: 'hide-backdrop' })
  hideBackdrop: boolean = DEFAULTS.HIDE_BACKDROP;

  /**
   * The name of the stack group this dialog belongs to.
   * Dialogs with the same stack group name will be grouped together.
   */
  @property({ type: String, attribute: 'stack-group-name', reflect: true })
  stackGroupName: string = '';

  /**
   * For now preventScroll is always true as the dialog is a modal component only.
   * This means scroll will be prevented when the dialog is open.
   */
  /** @internal */
  protected override preventScroll: boolean = true;

  /** @internal */
  protected triggerElement: HTMLElement | null = null;

  /** @internal */
  protected lastActiveElement: HTMLElement | null = null;

  override connectedCallback() {
    super.connectedCallback();

    // event listener can be added to the element directly, since dialog is a modal component
    // and it will not be allowed to be focused outside of it
    this.addEventListener('keydown', this.onEscapeKeydown.bind(this));
  }

  override disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.onEscapeKeydown);

    this.removeBackdrop();

    // Set aria-expanded attribute on the trigger element to false if it exists
    this.triggerElement?.setAttribute('aria-expanded', 'false');

    this.deactivatePreventScroll();

    this.deactivateFocusTrap?.();
    this.focusBackToTrigger();

    DialogEventManager.onDestroyedDialog(this);
  }

  /**
   * Applies the z-index to the dialog element.
   *
   * @internal
   */
  private applyZIndex() {
    this.style.setProperty('z-index', `${this.zIndex}`);
  }

  protected override async firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);

    this.setAttribute('aria-modal', 'true');

    this.setupAriaLabelledDescribedBy();

    this.applyZIndex();

    DialogEventManager.onCreatedDialog(this);
  }

  protected override async updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('triggerID')) {
      this.triggerElement = (this.getRootNode() as Document | ShadowRoot).querySelector(`[id="${this.triggerID}"]`);
      this.setupAriaHasPopup();
    }

    if (changedProperties.has('visible')) {
      const oldValue = changedProperties.get('visible') as boolean | undefined;
      await this.isOpenUpdated(oldValue, this.visible);
    }

    if (changedProperties.has('zIndex')) {
      // If zIndex is not set, use the default value
      // This is to ensure that the dialog has always a z-index set even if not explicitly defined
      if (this.zIndex === null) {
        this.zIndex = DEFAULTS.Z_INDEX;
      }
      this.applyZIndex();
    }

    if (changedProperties.has('variant')) {
      this.updateFooterButtonColors(this.variant);
    }

    if (
      changedProperties.has('ariaLabel') ||
      changedProperties.has('ariaLabelledby') ||
      changedProperties.has('ariaDescribedby') ||
      changedProperties.has('ariaDescription') ||
      changedProperties.has('headerText') ||
      changedProperties.has('descriptionText')
    ) {
      this.setupAriaLabelledDescribedBy();
    }

    if (changedProperties.has('focusTrap')) {
      // if focusTrap turned false and the popover is visible, deactivate the focus trap
      if (!this.focusTrap && this.visible) {
        this.deactivateFocusTrap();
      }
    }
  }

  /**
   * Sets up the aria-haspopup attribute for the dialog.
   * This is used to indicate that the dialog can be opened by a trigger element.
   * If the trigger element does not have an aria-haspopup attribute, it will default to 'dialog'.
   *
   * @internal
   */
  private setupAriaHasPopup() {
    if (!this.disableAriaHasPopup) {
      this.triggerElement?.setAttribute(
        'aria-haspopup',
        this.triggerElement?.getAttribute('aria-haspopup') || 'dialog',
      );
    }
  }

  /**
   * Sets up the aria-labelledby and aria-describedby attributes for the dialog.
   * If no header text or description text is provided, it will point to the the triggerID if available.
   * If neither is provided, it will not set the attributes.
   *
   * @internal
   */
  private setupAriaLabelledDescribedBy() {
    // If aria-labelledby or aria-label is already set, do not override it
    if (!this.ariaLabelledby && !this.ariaLabel) {
      if (this.headerText) {
        this.setAttribute('aria-label', this.headerText);
      } else if (this.triggerID) {
        this.setAttribute('aria-labelledby', this.triggerID);
      }
    }

    // If aria-describedby or aria-description is already set, do not override it
    if (!this.ariaDescribedby && !this.ariaDescription) {
      if (this.descriptionText) {
        this.setAttribute('aria-description', this.descriptionText);
      } else if (this.triggerID) {
        this.setAttribute('aria-describedby', this.triggerID);
      }
    }
  }

  /**
   * Fired when Close Button is clicked or Escape key is pressed.
   * This method dispatches the close event. Setting visible to false
   * has to be done by the consumer of the component.
   */
  private closeDialog() {
    this.depthManager.popHost();

    DialogEventManager.onCloseDialog(this, false);
  }

  /**
   * Handles the escape keydown event to close the dialog.
   * @internal
   * @param event - The keyboard event.
   */
  private onEscapeKeydown = (event: KeyboardEvent) => {
    if (!this.visible || this.getActionForKeyEvent(event) !== ACTIONS.ESCAPE || !this.depthManager.isHostOnTop()) {
      return;
    }

    event.preventDefault();
    // Prevent the event from propagating to the document level
    // pressing escape on a dialog should only close the dialog, nothing else
    event.stopPropagation();
    this.keyDownEventHandled();
    this.closeDialog();
  };

  /** @internal */
  onComponentStackChanged(changed: StackChange): void {
    if (changed === 'removed') {
      this.visible = false;
    } else if (changed === 'moved') {
      this.requestUpdate('zIndex');
    }
  }

  /**
   * Handles the dialog visibility change.
   * Handles the exit event to close the dialog.
   *
   * @param oldValue - The old value of the visible property.
   * @param newValue - The new value of the visible property.
   */
  private async isOpenUpdated(oldValue: boolean | undefined, newValue: boolean) {
    if (oldValue === newValue) {
      return;
    }

    if (newValue && !oldValue) {
      if (this.depthManager.pushHost()) {
        // request update to trigger zIndex recalculation
        this.requestUpdate('zIndex');
      }
      // Store the currently focused element before opening the dialog
      this.lastActiveElement = document.activeElement as HTMLElement;

      // create backdrop if enabled
      if (!this.hideBackdrop) {
        // remove any existing backdrop and create a new one
        this.removeBackdrop();
        this.createBackdrop('dialog');
      }

      this.activatePreventScroll();

      await this.updateComplete;
      if (this.focusTrap) {
        this.activateFocusTrap?.();
      }
      this.setInitialFocus?.();

      // Set aria-expanded attribute on the trigger element to true if it exists
      this.triggerElement?.setAttribute('aria-expanded', 'true');

      DialogEventManager.onShowDialog(this);
    } else if (!newValue && oldValue) {
      this.depthManager.popHost();

      // Always remove backdrop if it exists, regardless of current hideBackdrop value
      // This handles the case where hideBackdrop was changed while dialog was open
      this.removeBackdrop();

      // Set aria-expanded attribute on the trigger element to false if it exists
      this.triggerElement?.setAttribute('aria-expanded', 'false');

      this.deactivatePreventScroll();

      this.deactivateFocusTrap?.();
      this.focusBackToTrigger();

      DialogEventManager.onHideDialog(this);
    }
  }

  /**
   * Sets the focus back to the trigger element or the last active element.
   * This is called when the dialog is closed to ensure that the user can continue interacting with the application.
   *
   * @internal
   */
  private focusBackToTrigger() {
    // If the trigger element is defined, focus it
    if (this.triggerElement) {
      this.triggerElement.focus();
    } else if (this.lastActiveElement && this.lastActiveElement.focus) {
      // If the trigger element is not defined, focus the last active element
      this.lastActiveElement.focus();
    }
  }

  /**
   * Abstracting the renderHeader to allow for overrides of
   * extending components
   * @internal
   */
  protected renderHeader() {
    return this.headerText
      ? html` <div part="header-section">
          <div part="header">
            <slot name="header-prefix"></slot>
            <mdc-text
              part="header-text"
              tagname="${VALID_TEXT_TAGS[this.headerTagName.toUpperCase() as keyof typeof VALID_TEXT_TAGS]}"
              type="${TYPE.BODY_LARGE_BOLD}"
            >
              ${this.headerText}
            </mdc-text>
          </div>
          ${this.descriptionText
            ? html`<mdc-text
                part="description-text"
                tagname="${VALID_TEXT_TAGS[this.descriptionTagName.toUpperCase() as keyof typeof VALID_TEXT_TAGS]}"
                type="${TYPE.BODY_MIDSIZE_REGULAR}"
              >
                ${this.descriptionText}
              </mdc-text>`
            : nothing}
        </div>`
      : nothing;
  }

  /**
   * Abstracting the renderBody to allow for overrides of
   * extending components
   * @internal
   */
  protected renderBody() {
    return html` <div part="body">
      <slot name="dialog-body"></slot>
    </div>`;
  }

  public override render() {
    return html`
      ${this.renderHeader()}
      <mdc-button
        part="dialog-close-btn"
        prefix-icon="${DEFAULTS.CANCEL_ICON}"
        variant="${BUTTON_VARIANTS.TERTIARY}"
        size="${ICON_BUTTON_SIZES[20]}"
        aria-label="${ifDefined(this.closeButtonAriaLabel) || ''}"
        @click="${this.closeDialog}"
      ></mdc-button>
      ${this.renderBody()} ${this.renderFooter()}
    `;
  }

  public static override styles: Array<CSSResult> = [...Component.styles, ...styles];
}

export default Dialog;
